import * as plist from 'plist';
import React, { useMemo, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';

export type ProxySettings = {
  server: string;
  port: string;
  username: string;
  password: string;
};

type FacilityProfile = { data?: string };

type FacilityProxyFormProps = {
  facilityProfile?: FacilityProfile | null;
  saving?: boolean;
  deleting?: boolean;
  onSave?: (settings: ProxySettings) => void;
  onDelete?: () => void;
};

const FacilityProxyForm: React.FC<FacilityProxyFormProps> = (props) => {
  const { facilityProfile, saving, deleting, onSave, onDelete } = props;

  const initialSettings = useMemo((): ProxySettings => {
    if (!facilityProfile?.data) {
      return { server: '', port: '', username: '', password: '' };
    }
    try {
      const parsed = plist.parse(facilityProfile.data) as { PayloadContent?: Array<Record<string, unknown>> };
      const content = parsed?.PayloadContent?.[0];
      if (!content) {
        return { server: '', port: '', username: '', password: '' };
      }
      return {
        server: String(content.ProxyServer ?? ''),
        port: String(content.ProxyServerPort ?? ''),
        username: String(content.ProxyUsername ?? ''),
        password: String(content.ProxyPassword ?? '')
      };
    } catch {
      return { server: '', port: '', username: '', password: '' };
    }
  }, [facilityProfile?.data]);

  const [server, setServer] = useState(initialSettings.server);
  const [port, setPort] = useState(initialSettings.port);
  const [username, setUsername] = useState(initialSettings.username);
  const [password, setPassword] = useState(initialSettings.password);

  React.useEffect(() => {
    setServer(initialSettings.server);
    setPort(initialSettings.port);
    setUsername(initialSettings.username);
    setPassword(initialSettings.password);
  }, [initialSettings.server, initialSettings.port, initialSettings.username, initialSettings.password]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave?.({ server, port, username, password });
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Card>
        <Card.Header className="bg-info text-white">Proxy Configuration</Card.Header>
        <Card.Body>
          <Row>
            <Col md={3}>
              <Form.Group className="mb-2">
                <Form.Label>Server</Form.Label>
                <Form.Control
                  value={server}
                  onChange={(e) => setServer(e.target.value)}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group className="mb-2">
                <Form.Label>Port</Form.Label>
                <Form.Control
                  type="text"
                  value={port}
                  onChange={(e) => setPort(e.target.value)}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group className="mb-2">
                <Form.Label>Username</Form.Label>
                <Form.Control
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group className="mb-2">
                <Form.Label>Password</Form.Label>
                <Form.Control
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
        <Card.Footer>
          <Button type="submit" variant="primary" disabled={saving || deleting}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
          {facilityProfile && (
            <Button
              type="button"
              variant="danger"
              className="ms-2"
              disabled={deleting || saving}
              onClick={() => onDelete?.()}>
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          )}
        </Card.Footer>
      </Card>
    </Form>
  );
};

export default FacilityProxyForm;
